import * as Alert from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/heading'
import { Spinner } from '@/components/ui/spinner'
import { Text } from '@/components/ui/text'
import type { CreateSetup } from '@hvf/contracts'
import { BookOpenCheck, Pencil, RefreshCcw, TriangleAlert } from 'lucide-solid'
import { Match, Switch } from 'solid-js'
import { useService } from 'solid-services'
import { css } from 'styled-system/css'
import { Flex, Grid } from 'styled-system/jsx'
import { FormSteps, useCreate } from '../state'

export default function Success() {
  const {
    createError,
    createSuccess,
    setupMutation,
    isEditing,
    setStep,
    newSetup,
    setupBeingEdited
  } = useService(useCreate)()

  const handleRetry = () => {
    const current = newSetup()
    if (current) {
      setupMutation.mutate({
        setup: current as CreateSetup,
        oldSetup: setupBeingEdited()
      })
    }
  }

  return (
    <Grid
      mt={6}
      justifyItems="center"
    >
      <Switch>
        <Match when={createError()}>
          <Alert.Root>
            <Alert.Indicator asChild={(iconProps) => <TriangleAlert {...iconProps()} />} />
            <Alert.Content>
              <Alert.Title>
                {isEditing() ? 'Error updating Setup' : 'Error creating Setup'}
              </Alert.Title>
              <Alert.Description>{createError()}</Alert.Description>
            </Alert.Content>
          </Alert.Root>
          <Text mt={4} mb={6} textAlign="center">
            Please update the specified fields and resubmit the form, or try again.
          </Text>
          <Flex gap={4}>
            <Button variant="outline" onClick={() => setStep(FormSteps.details)}>
              <Pencil />
              Edit Details
            </Button>
            <Button onClick={handleRetry} loading={setupMutation.isPending}>
              <RefreshCcw />
              Try Again
            </Button>
          </Flex>
        </Match>

        <Match when={setupMutation.isPending}>
          <Spinner />
          <Text mt={4}>{isEditing() ? 'Editing Setup...' : 'Creating Setup...'}</Text>
        </Match>

        <Match when={createSuccess()}>
          <BookOpenCheck
            size={60}
            class={css({
              color: 'jade.10',
            })}
          />
          <Heading textStyle="lg" mt={4}>Success!</Heading>
          <Text mt={2} textAlign="center">
            {isEditing()
              ? 'This setup has been edited.'
              : 'This setup has been created.'}
          </Text>
        </Match>
      </Switch>
    </Grid>
  )
}
